/**
 * Zoekfilters (MA-27): oppervlakte, budget en energielabel in de zoekopdrachten
 * van het zoekgebied.
 *
 * Het zoekgebied (MA-25) zoekt op gebied alleen, en heel Amsterdam is dan 263
 * pagina's per ronde: te veel verkeer naar Funda. Eén set filters voor alle
 * zoekopdrachten van het zoekgebied - minimale woonoppervlakte, maximale
 * vraagprijs, energielabels - gaat mee in de Funda-URL, zodat Funda zelf minder
 * pagina's teruggeeft. Handmatige zoekopdrachten blijven ongemoeid.
 *
 * ## De URL-grammatica (nagemeten 9 oktober 2026)
 *
 * Funda schrijft zijn criteria in de adresbalk JSON-gecodeerd, net als
 * `selected_area`:
 *
 *     price="0-600000"              -> criteria.price       { min: 0, max: 600000 }
 *     floor_area="70-"              -> criteria.floor_area  { min: 70, max: null }
 *     energy_label=["D","E","F","G"] -> criteria.energy_label ["D","E","F","G"]
 *
 * De drie namen staan zo in `pinia.search.criteria` van elke zoekpagina
 * (`__NUXT_DATA__`); de waardevorm is met één pagina in een gewone Chrome
 * nagekeken: `selected_area=["1054"]&price="0-600000"&floor_area="70-"&
 * energy_label=["D","E","F","G"]` gaf precies die criteria terug, met 1 woning
 * tegen 144 zonder filters. Funda herschrijft die URL daarna naar de kale vorm
 * (`price=0-600000&floor_area=70-&energy_label=D,E,F,G`) - en dat is een
 * server-redirect: twee HTML-verzoeken per pagina. De kale vorm geeft die
 * redirect niet (MA-28). Beide vormen worden hier gelezen; sinds MA-28 wordt
 * de kale **kommavorm** geschreven.
 *
 * Energielabels: Funda kent A+ tot A+++++ als losse waarden. Hier kies je op
 * groep (A t/m G), en groep A schrijft alle A-varianten in de URL. Dat de
 * A-varianten ook als losse waarde in `energy_label` werken is niet nagemeten;
 * de standaardkeuze (D t/m G) raakt dat niet.
 */
import { z } from 'zod';
import { ENERGIELABELS, type Energielabel, isEnergielabel } from './energielabel';
import { fundaUrlTekst } from './zoekopdracht';

/* --- het contract ---------------------------------------------------------- */

/** De groepen waarop je kiest; A dekt A, A+, A++ ... A+++++. */
export const LABELGROEPEN = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const;
export type Labelgroep = (typeof LABELGROEPEN)[number];
export const labelgroepSchema = z.enum(LABELGROEPEN);

/** Standaard: D t/m G aan, A t/m C uit ("ik wil geen A t/m C"). */
export const ZOEKFILTER_LABELS_STANDAARD: readonly Labelgroep[] = ['D', 'E', 'F', 'G'];

export const ZOEKFILTER_M2_MAX = 10_000;
export const ZOEKFILTER_PRIJS_MAX = 100_000_000;

/**
 * Eén set filters. `null` is geen filter; een lege labellijst is ook geen
 * filter (alles mag). Een gevulde labellijst sluit woningen zonder label uit,
 * net als bij Funda.
 */
export const zoekfiltersSchema = z.object({
  minOppervlakteM2: z.number().int().positive().max(ZOEKFILTER_M2_MAX).nullable(),
  maxVraagprijs: z.number().int().positive().max(ZOEKFILTER_PRIJS_MAX).nullable(),
  energielabels: z.array(labelgroepSchema).max(LABELGROEPEN.length),
});
export type Zoekfilters = z.infer<typeof zoekfiltersSchema>;

export const ZOEKFILTERS_STANDAARD: Zoekfilters = {
  minOppervlakteM2: null,
  maxVraagprijs: null,
  energielabels: [...ZOEKFILTER_LABELS_STANDAARD],
};

export const ZOEKFILTERS_LEEG: Zoekfilters = {
  minOppervlakteM2: null,
  maxVraagprijs: null,
  energielabels: [],
};

/** Dezelfde filters als querystring, voor GET /beheer/zoekgebied/schatting. */
export const zoekfiltersQuerySchema = z.object({
  minOppervlakteM2: z.coerce.number().int().positive().max(ZOEKFILTER_M2_MAX).optional(),
  maxVraagprijs: z.coerce.number().int().positive().max(ZOEKFILTER_PRIJS_MAX).optional(),
  /** Komma-gescheiden groepen: `D,E,F,G`. Leeg of weggelaten is geen labelfilter. */
  energielabels: z
    .string()
    .optional()
    .transform((waarde) =>
      (waarde ?? '')
        .split(',')
        .map((deel) => deel.trim().toUpperCase())
        .filter((deel): deel is Labelgroep => (LABELGROEPEN as readonly string[]).includes(deel)),
    ),
});

export function zoekfiltersUitQuery(query: z.infer<typeof zoekfiltersQuerySchema>): Zoekfilters {
  return normaliseerZoekfilters({
    minOppervlakteM2: query.minOppervlakteM2 ?? null,
    maxVraagprijs: query.maxVraagprijs ?? null,
    energielabels: query.energielabels,
  });
}

export function zoekfiltersNaarQuery(filters: Zoekfilters): Record<string, string> {
  const uit: Record<string, string> = {};
  if (filters.minOppervlakteM2 != null) uit.minOppervlakteM2 = String(filters.minOppervlakteM2);
  if (filters.maxVraagprijs != null) uit.maxVraagprijs = String(filters.maxVraagprijs);
  if (filters.energielabels.length > 0) uit.energielabels = filters.energielabels.join(',');
  return uit;
}

/* --- labels ---------------------------------------------------------------- */

/** `A+++` -> `A`; `D` -> `D`. */
export function labelgroepVan(label: Energielabel): Labelgroep {
  return label.charAt(0) as Labelgroep;
}

/** De losse labels die een groep dekt, in de volgorde van ENERGIELABELS. */
export function labelsVanGroepen(groepen: readonly Labelgroep[]): Energielabel[] {
  const set = new Set(groepen);
  return ENERGIELABELS.filter((label) => set.has(labelgroepVan(label)));
}

/* --- vergelijken en normaliseren ------------------------------------------- */

/** Labels op volgorde en zonder dubbelen, zodat twee sets vergelijkbaar zijn. */
export function normaliseerZoekfilters(filters: Zoekfilters): Zoekfilters {
  const set = new Set(filters.energielabels);
  return {
    minOppervlakteM2: filters.minOppervlakteM2 ?? null,
    maxVraagprijs: filters.maxVraagprijs ?? null,
    energielabels: LABELGROEPEN.filter((groep) => set.has(groep)),
  };
}

export function zelfdeZoekfilters(a: Zoekfilters, b: Zoekfilters): boolean {
  const x = normaliseerZoekfilters(a);
  const y = normaliseerZoekfilters(b);
  return (
    x.minOppervlakteM2 === y.minOppervlakteM2 &&
    x.maxVraagprijs === y.maxVraagprijs &&
    x.energielabels.length === y.energielabels.length &&
    x.energielabels.every((groep, i) => groep === y.energielabels[i])
  );
}

/** Of er iets te filteren valt. */
export function heeftZoekfilters(filters: Zoekfilters | null | undefined): boolean {
  if (!filters) return false;
  return (
    filters.minOppervlakteM2 != null ||
    filters.maxVraagprijs != null ||
    filters.energielabels.length > 0
  );
}

/* --- een woning toetsen ---------------------------------------------------- */

export type ToetsbareWoning = {
  vraagprijs: number;
  oppervlakteM2: number | null;
  energielabel: Energielabel | null;
};

/**
 * Zou Funda deze woning met deze filters teruggeven? Dezelfde regel als de
 * SQL in @mamimap/db die de bekende woningen telt; een woning zonder
 * oppervlakte of zonder label valt bij een filter daarop af, net als bij Funda.
 */
export function voldoetAanZoekfilters(woning: ToetsbareWoning, filters: Zoekfilters): boolean {
  if (filters.maxVraagprijs != null && woning.vraagprijs > filters.maxVraagprijs) return false;
  if (
    filters.minOppervlakteM2 != null &&
    (woning.oppervlakteM2 == null || woning.oppervlakteM2 < filters.minOppervlakteM2)
  ) {
    return false;
  }
  if (filters.energielabels.length > 0) {
    if (woning.energielabel == null) return false;
    if (!filters.energielabels.includes(labelgroepVan(woning.energielabel))) return false;
  }
  return true;
}

/* --- de URL ---------------------------------------------------------------- */

const PRICE = 'price';
const FLOOR_AREA = 'floor_area';
const ENERGY_LABEL = 'energy_label';
const FUNDA_BASIS = 'https://www.funda.nl/zoeken/koop';

/**
 * Dezelfde URL, met deze filters in de kale kommavorm (`price=0-600000`,
 * `floor_area=70-`, `energy_label=D,E,F,G`). Alleen `price`, `floor_area` en
 * `energy_label` worden gezet of weggehaald; `selected_area` en de rest blijven
 * staan. `search_result` (de pagina) gaat eruit: pagina 1 draagt die niet.
 */
export function urlMetZoekfilters(url: string | null | undefined, filters: Zoekfilters): string {
  let adres: URL;
  try {
    adres = new URL(url || FUNDA_BASIS);
  } catch {
    adres = new URL(FUNDA_BASIS);
  }
  adres.searchParams.delete('search_result');

  const schoon = normaliseerZoekfilters(filters);
  if (schoon.maxVraagprijs != null) {
    adres.searchParams.set(PRICE, `0-${schoon.maxVraagprijs}`);
  } else {
    adres.searchParams.delete(PRICE);
  }
  if (schoon.minOppervlakteM2 != null) {
    adres.searchParams.set(FLOOR_AREA, `${schoon.minOppervlakteM2}-`);
  } else {
    adres.searchParams.delete(FLOOR_AREA);
  }
  if (schoon.energielabels.length > 0) {
    adres.searchParams.set(ENERGY_LABEL, labelsVanGroepen(schoon.energielabels).join(','));
  } else {
    adres.searchParams.delete(ENERGY_LABEL);
  }
  return fundaUrlTekst(adres);
}

/**
 * De filters uit een Funda-zoek-URL, in de JSON-vorm (`price="0-600000"`, van
 * vóór MA-28 of uit de adresbalk geplakt) of de kale kommavorm
 * (`price=0-600000`, `energy_label=D,E,F,G`). Een URL zonder filters geeft de
 * lege set; een URL die niet te lezen is ook - dan is er in elk geval niets
 * gefilterd.
 *
 * Een ondergrens op de prijs of een bovengrens op de oppervlakte kent het
 * contract niet; die worden hier genegeerd. Zo'n URL komt alleen uit een
 * handmatige zoekopdracht, en die beheert het zoekgebied niet.
 */
export function zoekfiltersUitUrl(url: string | null | undefined): Zoekfilters {
  if (!url) return ZOEKFILTERS_LEEG;
  let adres: URL;
  try {
    adres = new URL(url);
  } catch {
    return ZOEKFILTERS_LEEG;
  }

  const prijs = leesBereik(adres.searchParams.get(PRICE));
  const oppervlakte = leesBereik(adres.searchParams.get(FLOOR_AREA));
  const labels = leesLijst(adres.searchParams.get(ENERGY_LABEL))
    .map((waarde) => waarde.toUpperCase())
    .filter(isEnergielabel)
    .map(labelgroepVan);

  return normaliseerZoekfilters({
    minOppervlakteM2: oppervlakte.min != null && oppervlakte.min > 0 ? oppervlakte.min : null,
    maxVraagprijs: prijs.max ?? null,
    energielabels: labels,
  });
}

/** `"70-"` / `70-` / `"0-600000"` -> { min, max }; iets anders -> leeg. */
function leesBereik(ruw: string | null): { min: number | null; max: number | null } {
  if (!ruw) return { min: null, max: null };
  const tekst = ruw.trim().replace(/^"|"$/g, '');
  const m = /^(\d*)-(\d*)$/.exec(tekst);
  if (!m) return { min: null, max: null };
  const min = m[1] ? Number(m[1]) : null;
  const max = m[2] ? Number(m[2]) : null;
  return {
    min: min != null && Number.isInteger(min) ? min : null,
    max: max != null && Number.isInteger(max) ? max : null,
  };
}

/** `["D","E"]` of `D,E` -> `['D','E']`. */
function leesLijst(ruw: string | null): string[] {
  if (!ruw) return [];
  const tekst = ruw.trim();
  if (tekst.startsWith('[')) {
    try {
      const lijst: unknown = JSON.parse(tekst);
      if (Array.isArray(lijst)) return lijst.filter((x): x is string => typeof x === 'string');
    } catch {
      /* Dan de kale vorm met haakjes als ruis. */
    }
    return tekst.replace(/^\[|\]$/g, '').split(',');
  }
  return tekst
    .split(',')
    .map((deel) => deel.trim().replace(/^"|"$/g, ''))
    .filter(Boolean);
}

/* --- het aandeel ----------------------------------------------------------- */

/** Per postcode: hoeveel woningen we actief kennen, en hoeveel daarvan voldoen. */
export type BekendTelling = { totaal: number; voldoet: number };

/**
 * Onder dit aantal bekende woningen in een postcode zegt het eigen aandeel te
 * weinig; dan geldt het aandeel over het hele gebied.
 */
export const MIN_BEKEND_VOOR_AANDEEL = 5;

/**
 * Welk deel van Funda's woningen per postcode door de filters komt, geschat
 * aan de woningen die we er al kennen. Zonder bekende woningen geen correctie
 * (aandeel 1): dan is er niets om op te rekenen.
 */
export function aandeelPerPostcode(
  postcodes: readonly string[],
  bekend: ReadonlyMap<string, BekendTelling>,
  minimum = MIN_BEKEND_VOOR_AANDEEL,
): Map<string, number> {
  let totaal = 0;
  let voldoet = 0;
  for (const pc of postcodes) {
    const telling = bekend.get(pc);
    if (!telling) continue;
    totaal += telling.totaal;
    voldoet += telling.voldoet;
  }
  const overal = totaal > 0 ? voldoet / totaal : 1;

  const uit = new Map<string, number>();
  for (const pc of postcodes) {
    const telling = bekend.get(pc);
    uit.set(pc, telling && telling.totaal >= minimum ? telling.voldoet / telling.totaal : overal);
  }
  return uit;
}

/** De schatting per postcode maal het aandeel dat door de filters komt. */
export function pasAandeelToe(
  schatting: ReadonlyMap<string, number>,
  aandeel: ReadonlyMap<string, number>,
): Map<string, number> {
  const uit = new Map<string, number>();
  for (const [pc, n] of schatting) uit.set(pc, n * (aandeel.get(pc) ?? 1));
  return uit;
}

/* --- leesbaar -------------------------------------------------------------- */

/** `≥ 70 m², tot € 600.000, label D-G` - voor de regel bij de teller. */
export function zoekfiltersLabel(filters: Zoekfilters): string {
  const delen: string[] = [];
  if (filters.minOppervlakteM2 != null) {
    delen.push(`≥ ${new Intl.NumberFormat('nl-NL').format(filters.minOppervlakteM2)} m²`);
  }
  if (filters.maxVraagprijs != null) {
    delen.push(
      `tot ${new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(filters.maxVraagprijs)}`,
    );
  }
  const labels = normaliseerZoekfilters(filters).energielabels;
  if (labels.length > 0) delen.push(`label ${compacteLabels(labels)}`);
  return delen.join(', ');
}

/** `['D','E','F','G']` -> `D-G`; `['A','C','D']` -> `A, C-D`. */
export function compacteLabels(groepen: readonly Labelgroep[]): string {
  const gesorteerd = normaliseerZoekfilters({
    ...ZOEKFILTERS_LEEG,
    energielabels: [...groepen],
  }).energielabels;
  const index = (groep: Labelgroep) => LABELGROEPEN.indexOf(groep);
  const delen: string[] = [];
  let i = 0;
  while (i < gesorteerd.length) {
    let j = i;
    while (j + 1 < gesorteerd.length) {
      const volgende = gesorteerd[j + 1];
      const huidige = gesorteerd[j];
      if (!volgende || !huidige || index(volgende) !== index(huidige) + 1) break;
      j++;
    }
    const van = gesorteerd[i] ?? '';
    const tot = gesorteerd[j] ?? '';
    delen.push(i === j ? van : `${van}-${tot}`);
    i = j + 1;
  }
  return delen.join(', ');
}
